/**
 * TREATX-NOTIFICATION-ADMIN-OPS-11B
 * ----------------------------------
 * NotificationsLogViewer
 *
 * Thin admin UI over the existing `GET /api/admin/notifications`
 * endpoint. NOT a new log store. NOT a new event registry.
 *
 * Filters: event / channel / status / recipient. Pagination via the
 * existing endpoint's `skip` + `limit`. A click on a row opens a
 * read-only drawer showing the full notifications_log document.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { formatDateTimeLocale } from '../../utils/dateFormat';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Search, X, Filter, RefreshCw, Loader2,
  Mail, MessageSquare, Bell, ShieldOff, AlertTriangle, CheckCircle2,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import api from '../../utils/api';

const CHANNEL_ICON = {
  email: Mail,
  sms: MessageSquare,
  whatsapp: MessageSquare,
  in_app: Bell,
};

const STATUS_VARIANT = (s) => {
  const v = (s || '').toLowerCase();
  if (v.includes('sent')) return 'bg-emerald-100 text-emerald-700';
  if (v.includes('fail')) return 'bg-rose-100 text-rose-700';
  if (v.includes('block')) return 'bg-amber-100 text-amber-700';
  if (v.includes('queue')) return 'bg-sky-100 text-sky-700';
  return 'bg-slate-100 text-slate-700';
};

const PAGE_SIZE = 25;

const NotificationsLogViewer = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [skip, setSkip] = useState(0);
  const [filters, setFilters] = useState({ event: '', channel: '', status: '', recipient: '' });
  const [selected, setSelected] = useState(null);

  const params = useMemo(() => {
    const p = { limit: PAGE_SIZE, skip };
    Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  }, [skip, filters]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/notifications', { params });
      setRows(r.data?.notifications || []);
      setTotal(r.data?.total || 0);
    } catch (e) {
      setRows([]); setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [skip, filters]);

  const updateFilter = (k, v) => { setSkip(0); setFilters(prev => ({ ...prev, [k]: v })); };
  const reset = () => { setSkip(0); setFilters({ event: '', channel: '', status: '', recipient: '' }); };

  const fmt = (iso) => {
    if (!iso) return '';
    try { return formatDateTimeLocale(iso); } catch { return iso; }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="notifications-log-viewer">
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/treatx-admin/notification-ops')}
              className="p-2 rounded-full hover:bg-slate-100 text-slate-500"
              data-testid="log-back-btn"
              aria-label="Back to notification ops"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <div>
              <h1 className="text-xl font-semibold text-slate-900">Notifications log</h1>
              <p className="text-xs text-slate-500">Single canonical view of <code className="font-mono">notifications_log</code>. No mirrored store.</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="log-refresh-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Refresh
          </Button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6 space-y-4">
        {/* Filters */}
        <div className="bg-white border rounded-2xl p-4" data-testid="log-filters">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-sm text-slate-700 font-medium">
              <Filter className="h-4 w-4 text-slate-500" /> Filters
            </div>
            <Button variant="ghost" size="sm" onClick={reset} data-testid="log-reset-filters-btn">
              <X className="h-3.5 w-3.5 mr-1" /> Reset
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Input
              placeholder="Event key…"
              value={filters.event}
              onChange={(e) => updateFilter('event', e.target.value)}
              data-testid="log-filter-event"
            />
            <select
              value={filters.channel}
              onChange={(e) => updateFilter('channel', e.target.value)}
              className="border border-slate-300 rounded-md px-3 py-2 text-sm bg-white"
              data-testid="log-filter-channel"
            >
              <option value="">Any channel</option>
              <option value="email">Email</option>
              <option value="sms">SMS</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="in_app">In-app</option>
            </select>
            <select
              value={filters.status}
              onChange={(e) => updateFilter('status', e.target.value)}
              className="border border-slate-300 rounded-md px-3 py-2 text-sm bg-white"
              data-testid="log-filter-status"
            >
              <option value="">Any status</option>
              <option value="sent">sent</option>
              <option value="SENT">SENT</option>
              <option value="failed">failed</option>
              <option value="FAILED">FAILED</option>
              <option value="blocked_by_gating">blocked_by_gating</option>
              <option value="blocked_by_consent">blocked_by_consent</option>
              <option value="queued">queued</option>
            </select>
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-3.5 w-3.5 text-slate-400" />
              <Input
                placeholder="Recipient email / phone"
                value={filters.recipient}
                onChange={(e) => updateFilter('recipient', e.target.value)}
                className="pl-8"
                data-testid="log-filter-recipient"
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border rounded-2xl overflow-hidden" data-testid="log-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-2 text-left">When</th>
                  <th className="px-4 py-2 text-left">Channel</th>
                  <th className="px-4 py-2 text-left">Event</th>
                  <th className="px-4 py-2 text-left">Recipient</th>
                  <th className="px-4 py-2 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400" data-testid="log-loading">
                    <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…
                  </td></tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400" data-testid="log-empty">
                    No notifications match this query.
                  </td></tr>
                )}
                {!loading && rows.map((r, idx) => {
                  const Icon = CHANNEL_ICON[r.channel] || Bell;
                  const rid = r.id || r.correlation_id || `row-${idx}`;
                  return (
                    <tr
                      key={rid}
                      onClick={() => setSelected(r)}
                      className="border-t hover:bg-slate-50 cursor-pointer"
                      data-testid={`log-row-${idx}`}
                    >
                      <td className="px-4 py-2 text-slate-600 whitespace-nowrap text-xs">{fmt(r.created_at)}</td>
                      <td className="px-4 py-2"><Icon className="h-3.5 w-3.5 inline mr-1 text-slate-500" /> {r.channel || '—'}</td>
                      <td className="px-4 py-2 font-mono text-xs text-slate-700">{r.event || '—'}</td>
                      <td className="px-4 py-2 text-xs text-slate-700">{r.recipient_email || r.recipient_phone || r.actor_id || '—'}</td>
                      <td className="px-4 py-2">
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${STATUS_VARIANT(r.status)}`}>
                          {r.status || 'unknown'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Pager */}
          <div className="flex items-center justify-between px-4 py-3 border-t bg-slate-50/60 text-xs text-slate-600">
            <span data-testid="log-pager-summary">
              {total === 0 ? '0 results' : `Showing ${Math.min(skip + 1, total)}–${Math.min(skip + rows.length, total)} of ${total}`}
            </span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={skip === 0 || loading} onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))} data-testid="log-prev-btn">Previous</Button>
              <Button variant="outline" size="sm" disabled={skip + rows.length >= total || loading} onClick={() => setSkip(skip + PAGE_SIZE)} data-testid="log-next-btn">Next</Button>
            </div>
          </div>
        </div>
      </div>

      {/* Detail drawer */}
      {selected && (
        <div
          className="fixed inset-0 bg-black/40 z-[70] flex items-stretch justify-end"
          data-testid="log-detail-drawer"
          onClick={() => setSelected(null)}
        >
          <div className="bg-white w-full max-w-md h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <h3 className="text-sm font-semibold text-slate-900">Notification detail</h3>
              <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-slate-700" data-testid="log-drawer-close-btn">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-3 text-sm">
              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${STATUS_VARIANT(selected.status)}`}>
                  {selected.status || 'unknown'}
                </span>
                {selected.channel === 'email' && <Mail className="h-4 w-4 text-slate-400" />}
                {selected.channel === 'sms' && <MessageSquare className="h-4 w-4 text-slate-400" />}
                {selected.channel === 'whatsapp' && <MessageSquare className="h-4 w-4 text-emerald-500" />}
                {(selected.status || '').includes('block') && <ShieldOff className="h-4 w-4 text-amber-500" />}
                {(selected.status || '').includes('SENT') && <CheckCircle2 className="h-4 w-4 text-emerald-500" />}
                {(selected.status || '').includes('FAIL') && <AlertTriangle className="h-4 w-4 text-rose-500" />}
              </div>
              <div>
                <div className="text-[11px] text-slate-500 uppercase tracking-wide">Event</div>
                <div className="font-mono text-xs">{selected.event || '—'}</div>
              </div>
              <div>
                <div className="text-[11px] text-slate-500 uppercase tracking-wide">Recipient</div>
                <div className="text-xs">{selected.recipient_email || selected.recipient_phone || selected.actor_id || '—'}</div>
              </div>
              <div>
                <div className="text-[11px] text-slate-500 uppercase tracking-wide">Created at</div>
                <div className="text-xs">{fmt(selected.created_at)}</div>
              </div>
              {selected.template_resolution && (
                <div>
                  <div className="text-[11px] text-slate-500 uppercase tracking-wide">Template resolution</div>
                  <div className="text-xs">{selected.template_resolution}</div>
                </div>
              )}
              {selected.correlation_id && (
                <div>
                  <div className="text-[11px] text-slate-500 uppercase tracking-wide">Correlation</div>
                  <div className="font-mono text-[11px]">{selected.correlation_id}</div>
                </div>
              )}
              <details className="pt-2 border-t" data-testid="log-drawer-raw">
                <summary className="text-xs text-slate-500 cursor-pointer">Raw record</summary>
                <pre className="text-[10px] bg-slate-50 p-2 rounded mt-2 whitespace-pre-wrap break-words max-h-72 overflow-auto">{JSON.stringify(selected, null, 2)}</pre>
              </details>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsLogViewer;
